import { AGENT_URL } from '@/config/env'
import {
  buildListSearchParams,
  fetchJsonWithAuth,
  isRecord,
  parsePagination,
} from '@/lib/api'
import type { ListQueryParams, Pagination } from '@/lib/api'
import type {
  ChronicConditionRecord,
  LabResultRecord,
} from '@/features/clinical-data/types'

export type ClinicalDataEndpoint = 'lab-results' | 'chronic-conditions'

export interface ClinicalDataPage<T> {
  data: T[]
  pagination: Pagination
}

function requiredString(row: Record<string, unknown>, field: string): string {
  const value = row[field]
  if (typeof value !== 'string') {
    throw new Error(`Clinical data response is missing ${field}`)
  }
  return value
}

function optionalString(
  row: Record<string, unknown>,
  field: string
): string | null {
  const value = row[field]
  if (value == null) return null
  if (typeof value !== 'string') {
    throw new Error(`Clinical data response has invalid ${field}`)
  }
  return value
}

export function mapLabResult(row: Record<string, unknown>): LabResultRecord {
  return {
    id: requiredString(row, 'id'),
    patient_name: requiredString(row, 'patient_name'),
    test_code: optionalString(row, 'test_code'),
    test_name: optionalString(row, 'test_name'),
    result_raw: requiredString(row, 'result_raw'),
    lab_vendor: optionalString(row, 'lab_vendor'),
    observed_at: requiredString(row, 'observed_at'),
  }
}

export function mapChronicCondition(
  row: Record<string, unknown>
): ChronicConditionRecord {
  return {
    id: requiredString(row, 'id'),
    patient_id: requiredString(row, 'patient_id'),
    patient_name: requiredString(row, 'patient_name'),
    source_system: requiredString(row, 'source_system'),
    classification_system: requiredString(row, 'classification_system'),
    condition_code: requiredString(row, 'condition_code'),
    condition_description: requiredString(row, 'condition_description'),
    effective_date: requiredString(row, 'effective_date'),
  }
}

export async function fetchClinicalDataPage<T>({
  token,
  endpoint,
  params,
  mapRow,
  cursor,
  signal,
}: {
  token: string
  endpoint: ClinicalDataEndpoint
  params: ListQueryParams
  mapRow: (row: Record<string, unknown>) => T
  cursor?: string
  signal?: AbortSignal
}): Promise<ClinicalDataPage<T>> {
  const search = buildListSearchParams(params, cursor)
  const response = await fetchJsonWithAuth<unknown>({
    token,
    url: `${AGENT_URL}/data/${endpoint}?${search.toString()}`,
    signal,
    errorMessage: `Failed to fetch ${endpoint}`,
  })

  if (!isRecord(response) || !Array.isArray(response.data)) {
    throw new Error(`Invalid ${endpoint} response`)
  }

  return {
    data: response.data.map((row) => {
      if (!isRecord(row)) throw new Error(`Invalid ${endpoint} row`)
      return mapRow(row)
    }),
    pagination: parsePagination(
      isRecord(response.pagination) ? response.pagination : null,
      params.limit ?? 50
    ),
  }
}
